import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "./command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"

export function Combobox({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  className,
  disabled = false,
  containsSearch = false,
  testId,
}) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")

  const selectedOption = options.find((option) => option.value === value)

  // containsSearch: plain case-insensitive "contains" on number OR name (like the list pages),
  // ranked exact match → starts with → contains; newest number first within each rank.
  const visibleOptions = React.useMemo(() => {
    if (!containsSearch) return options
    const q = query.trim().toLowerCase()
    if (!q) return options
    const rank = (o) => {
      const num = (o.number || "").toLowerCase()
      const name = (o.name || "").toLowerCase()
      if (num === q || name === q) return 0
      if (num.startsWith(q)) return 1
      if (name.startsWith(q)) return 2
      if (num.includes(q) || name.includes(q)) return 3
      return -1
    }
    return options
      .map((o) => ({ o, r: rank(o) }))
      .filter((x) => x.r >= 0)
      .sort((a, b) => a.r - b.r || (b.o.number || "").localeCompare(a.o.number || "", undefined, { numeric: true }))
      .map((x) => x.o)
  }, [options, query, containsSearch])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
          data-testid={testId}
        >
          <span className="truncate" title={selectedOption?.label}>{selectedOption ? selectedOption.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" style={{ width: 'var(--radix-popover-trigger-width)' }}>
        <Command shouldFilter={!containsSearch}>
          <CommandInput
            placeholder={searchPlaceholder}
            {...(containsSearch ? { value: query, onValueChange: setQuery } : {})}
            data-testid={testId ? `${testId}-search` : undefined}
          />
          <CommandEmpty>{emptyMessage}</CommandEmpty>
          <CommandGroup className="max-h-[300px] overflow-y-auto overflow-x-hidden">
            {visibleOptions.map((option) => (
              <CommandItem
                key={option.value}
                value={containsSearch ? option.value : option.label}
                onSelect={() => {
                  onChange(option.value === value ? "" : option.value)
                  setOpen(false)
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === option.value ? "opacity-100" : "opacity-0"
                  )}
                />
                {option.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
